import {
  concretePackageItemQuantity,
  countPackageItemPieces,
} from "@src/common/utils/packageItems";

export const STANDARD_SKID_LENGTH_INCHES = 48;
export const STANDARD_SKID_WIDTH_INCHES = 40;
export const DEFAULT_FREE_SKID_AMOUNT = 1;

/**
 * Mirrors the backend `SkidSpaceCalculator` (PR set 3): given a package's L
 * and W in inches, return the number of standard 48x40 skid spaces it
 * occupies. Returns `null` when either dimension is missing or zero so
 * callers can hide the skid-count label until both inputs are entered.
 *
 * The formula is rotation-invariant on L/W: dispatchers who put the long
 * side in either input get the same answer.
 */
export function calculateSkidSpaces(
  length: number | null | undefined,
  width: number | null | undefined,
): number | null {
  if (length == null || width == null) return null;
  if (length <= 0 || width <= 0) return null;

  const sL = STANDARD_SKID_LENGTH_INCHES;
  const sW = STANDARD_SKID_WIDTH_INCHES;

  const option1 = Math.ceil(length / sL) * Math.ceil(width / sW);
  const option2 = Math.ceil(length / sW) * Math.ceil(width / sL);

  return Math.min(option1, option2);
}

interface SkidSpaceItem {
  length?: number | null;
  width?: number | null;
  /** Optional per-item override; null / absent inherits the shipment type. */
  packageTypeId?: string | null;
  /** Pieces this row stands for; dimensions are per piece. Null reads as one piece. */
  quantity?: number | null;
}

/**
 * Quantity-aware order-level skid count. Every package occupies at least one
 * skid space, so the floor is the package quantity:
 * `max(packageCount, packageItems.length)` (the `max` guards the edge case where
 * the supplied quantity is fewer than the dimension entries). Each dimensioned
 * package that occupies more than one space replaces its default single space
 * with its computed value; packages with missing/partial dimensions stay at
 * their default one space.
 *
 * Mirrors the backend `SkidSpaceCalculator.countOrderSkidSpaces`. Intentionally
 * package-type-agnostic; the package-type gate is layered on by
 * `computeOrderSkidSpaces`.
 */
export function countOrderSkidSpaces(
  packageItems: SkidSpaceItem[] | null | undefined,
  packageCount: number | null | undefined,
): number {
  const items = packageItems ?? [];
  const pieceCount = countPackageItemPieces(items);
  let total = Math.max(packageCount ?? 0, pieceCount);
  for (const item of items) {
    const spaces = calculateSkidSpaces(item.length, item.width);
    if (spaces == null || spaces <= 0) continue;
    const pieces = concretePackageItemQuantity(item);
    total = total - pieces + spaces * pieces;
  }
  return total;
}

function isSkidPackageType(
  packageTypeId: string | null | undefined,
  skidPackageTypeIds: ReadonlySet<string>,
): boolean {
  return packageTypeId != null && skidPackageTypeIds.has(packageTypeId);
}

/**
 * Order-level skid estimate for display. Item rows are gated by their effective
 * package type (override ?: shipment type); packages counted by quantity but not
 * represented by a row inherit the shipment type. Returns `null` when neither
 * matching items nor matching extras exist, so callers hide the estimate.
 * `skidPackageTypeIds` are the courier's `SKID` category package types.
 *
 * Mirrors the backend `SkidSpaceCalculator.computeOrderSkidSpaces`.
 */
export function computeOrderSkidSpaces(
  packageItems: SkidSpaceItem[] | null | undefined,
  packageCount: number | null | undefined,
  packageTypeId: string | null | undefined,
  skidPackageTypeIds: ReadonlySet<string>,
): number | null {
  const items = packageItems ?? [];
  const skidItems = items.filter((item) =>
    isSkidPackageType(item.packageTypeId ?? packageTypeId, skidPackageTypeIds),
  );

  const pieceCount = countPackageItemPieces(items);
  const totalPackageCount = packageCount ?? pieceCount;
  const extraPackageCount = Math.max(totalPackageCount - pieceCount, 0);
  const skidPackageCount =
    countPackageItemPieces(skidItems) +
    (isSkidPackageType(packageTypeId, skidPackageTypeIds) ? extraPackageCount : 0);
  if (skidPackageCount === 0) return null;

  return skidItems.reduce((total, item) => {
    const spaces = calculateSkidSpaces(item.length, item.width);
    if (spaces == null || spaces <= 0) return total;
    const pieces = concretePackageItemQuantity(item);
    return total - pieces + spaces * pieces;
  }, skidPackageCount);
}
